import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Lock, BadgeCheck, CalendarClock, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import ListingCard from "@/components/ListingCard";
import { publicApi, VIBE_IMAGES } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1611821427154-edd4d40d7ea5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const HERO_2 = "https://images.unsplash.com/photo-1758797316894-c25b94e8426a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

function Hero() {
  return (
    <section className="mx-auto grid max-w-7xl gap-10 px-5 pb-16 pt-10 md:px-8 lg:grid-cols-[1.05fr_1fr] lg:pt-16">
      <div className="rise flex flex-col justify-center">
        <div className="eyebrow text-secondary">Curated getaways · Pakistan & beyond</div>
        <h1 className="mt-5 text-4xl leading-[1.05] tracking-tight text-primary sm:text-5xl lg:text-6xl">
          Know what your days look like <em className="text-secondary">before</em> you go.
        </h1>
        <p className="mt-6 max-w-lg text-base text-muted-foreground md:text-lg">
          All-inclusive escapes with every hour on the timeline, verified hosts behind every listing, and PKR pricing with nothing hidden. Your payment stays protected until the trip is delivered.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Button asChild size="lg" className="rounded-full bg-secondary px-7 hover:bg-secondary/90" data-testid="hero-guide-me-btn">
            <Link to="/guide-me"><Compass className="mr-2 h-4 w-4" />Guide me to a getaway</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-primary/30 px-7 hover:bg-primary hover:text-white" data-testid="hero-explore-btn">
            <Link to="/explore">Browse all escapes <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
      <div className="relative grid grid-cols-5 gap-3">
        <div className="col-span-3 row-span-2 overflow-hidden rounded-2xl"><img src={HERO} alt="Hunza valley in autumn" className="h-full min-h-[360px] w-full object-cover lg:min-h-[520px]" /></div>
        <div className="col-span-2 overflow-hidden rounded-2xl"><img src={HERO_2} alt="Meditation" className="h-full w-full object-cover" /></div>
        <div className="col-span-2 flex flex-col justify-end gap-5 rounded-2xl bg-primary p-5 text-white grain">
          <div className="eyebrow text-white/60">Day 2 · Hunza</div>
          <ul className="space-y-2 text-sm">
            <li><span className="tabular-nums text-white/60">06:15</span> Silent walk</li>
            <li><span className="tabular-nums text-white/60">10:30</span> Breathwork</li>
            <li><span className="tabular-nums text-white/60">14:00</span> Free afternoon</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function Vibes({ vibes }) {
  return (
    <section className="mx-auto max-w-7xl px-5 py-12 md:px-8" data-testid="vibes-section">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="eyebrow text-muted-foreground">Start with a feeling</div>
          <h2 className="mt-2 text-3xl tracking-tight text-primary md:text-4xl">Four vibes. That's the whole taxonomy.</h2>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {vibes.map((v, i) => (
          <Link key={v.key} to={`/explore?vibe=${v.key}`} className={`group relative overflow-hidden rounded-2xl ${i % 2 ? "lg:mt-10" : ""}`} data-testid={`vibe-card-${v.key}`}>
            <img src={VIBE_IMAGES[v.key]} alt={v.name} className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-0 p-5 text-white">
              <div className="font-display text-2xl">{v.name}</div>
              <p className="mt-1 text-sm text-white/80">{v.line}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Spotlight({ items }) {
  if (!items.length) return null;
  const [first, ...rest] = items;
  return (
    <section className="mx-auto max-w-7xl px-5 py-16 md:px-8" data-testid="spotlight-section">
      <div className="eyebrow text-secondary">Spotlight</div>
      <h2 className="mt-2 text-3xl tracking-tight text-primary md:text-4xl">Curated this season</h2>
      <div className="mt-10 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <ListingCard l={first} tall />
        <div className="grid gap-10 sm:grid-cols-2">
          {rest.slice(0, 4).map((l) => <ListingCard key={l.id} l={l} />)}
        </div>
      </div>
    </section>
  );
}

const PILLARS = [
  { icon: CalendarClock, t: "Timeline-first listings", d: "Every getaway is a structured schedule, day by day, hour by hour. No guessing." },
  { icon: BadgeCheck, t: "Verified operators", d: "KYC, signed contracts and verified payout accounts before any listing goes live." },
  { icon: Lock, t: "Payout-safe booking", d: "GOSHA is merchant of record. Partners are paid only after delivery is verified." },
  { icon: LifeBuoy, t: "Support after you pay", d: "Your guest portal carries logistics, live notices and a support line through the trip." },
];

function Pillars() {
  return (
    <section className="grain bg-primary py-20 text-white">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-[1fr_2fr]">
        <div>
          <div className="eyebrow text-white/50">Why GOSHA is stricter</div>
          <h2 className="mt-3 text-3xl tracking-tight md:text-4xl">Fewer listings. Each one operable.</h2>
          <p className="mt-4 text-white/70">We don't publish open-catalog supply. Every partner is approved and every getaway passes a compliance checklist first.</p>
        </div>
        <div className="grid gap-8 sm:grid-cols-2">
          {PILLARS.map((p) => (
            <div key={p.t} className="border-t border-white/15 pt-5">
              <p.icon className="h-5 w-5 text-secondary" strokeWidth={1.5} />
              <div className="mt-3 font-display text-xl">{p.t}</div>
              <p className="mt-2 text-sm text-white/70">{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Landers({ landers }) {
  return (
    <section className="mx-auto max-w-7xl px-5 py-20 md:px-8" data-testid="landers-section">
      <div className="eyebrow text-muted-foreground">Popular intents</div>
      <div className="mt-6 divide-y divide-border border-y border-border">
        {landers.map((l) => (
          <Link key={l.key} to={`/escapes/${l.key}`} className="group flex items-center justify-between py-6" data-testid={`lander-link-${l.key}`}>
            <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-6">
              <span className="font-display text-2xl tracking-tight text-primary md:text-3xl">{l.title}</span>
              <span className="text-sm text-muted-foreground">{l.kicker}</span>
            </div>
            <ArrowRight className="h-5 w-5 text-secondary transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [meta, setMeta] = useState({ vibes: [], landers: [] });
  const [spot, setSpot] = useState([]);
  useEffect(() => {
    publicApi.get("/meta").then((r) => setMeta(r.data));
    publicApi.get("/listings/spotlight").then((r) => setSpot(r.data));
  }, []);
  return (
    <div data-testid="home-page">
      <Hero />
      <Vibes vibes={meta.vibes} />
      <Spotlight items={spot} />
      <Pillars />
      <Landers landers={meta.landers} />
      <section className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-8 rounded-2xl border border-border bg-white p-8 md:grid-cols-[1fr_auto] md:p-12">
          <div>
            <div className="eyebrow text-secondary">For organizers, operators & curators</div>
            <h2 className="mt-2 text-2xl tracking-tight text-primary md:text-3xl">Host a getaway on GOSHA</h2>
            <p className="mt-2 max-w-xl text-muted-foreground">Apply, get verified, and build structured listings with an itinerary builder, roster tools and drafting help.</p>
          </div>
          <Button asChild size="lg" className="rounded-full px-7" data-testid="home-partner-cta"><Link to="/partner">Apply as a partner</Link></Button>
        </div>
      </section>
    </div>
  );
}
